import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import ArrowsDownToPeopleIcon from '@eversports/design-tokens/assets/icons/regular/arrows-down-to-people.svg'
import NoSsr from '@eversports/klimt-design-components/NoSsr'
import PromotionalTooltip from '@eversports/klimt-patterns/PromotionalTooltip'
import Icon from '@eversports/klimt-primitives/legacy/Icon'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Text from '@eversports/klimt-primitives/legacy/Text'
import { useState } from 'react'

import { Localized } from '../../../../localization/react'
import FavoriteAndShareButtonsBox from '../FavoriteAndShareButtons/components/FavoriteAndShareButtonsBox'

import { ErrorSnackbarPortal } from './ErrorSnackbarPortal'
import { useFriendReferralGenerateCode } from './hooks/useFriendReferralGenerateCode'
import useReferralTooltipOnFirstVisit from './hooks/useReferralTooltipOnFirstVisit'
import { useVenueReferAFriendData } from './hooks/useVenueReferAFriendData'
import { VenueReferAFriendOverlay } from './VenueReferAFriendOverlay'

export function VenueReferAFriendButton() {
  const { amplitude } = useAmplitude()
  const { venue, referral, isCustomer } = useVenueReferAFriendData()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { isTooltipOpen, closeTooltip } = useReferralTooltipOnFirstVisit({
    referralCode: referral?.code ?? undefined,
    companyId: venue?.company.id,
    isCustomer,
  })
  const {
    code: generatedCode,
    rewardDescription: generatedRewardDescription,
    hasError,
    onErrorClose,
    isGenerating,
    generateCodeIfNeeded,
  } = useFriendReferralGenerateCode()

  const venueName = venue?.name ?? ''
  const code = referral?.code ?? generatedCode ?? null
  const rewardDescription = referral?.rewardDescription ?? generatedRewardDescription ?? null
  const toggleDialog = () => setIsDialogOpen((prev) => !prev)

  if (!referral?.isActive) return null

  const handleClick = async () => {
    if (isGenerating || !venue) return

    closeTooltip()
    amplitude?.logEvent('Clicked Venue Profile Refer A Friend Button', {
      venue: { name: venue.name, slug: venue.slug },
    })

    if (code) {
      setIsDialogOpen(true)
      return
    }

    const success = await generateCodeIfNeeded(venue.company.id)
    if (success) {
      setIsDialogOpen(true)
    }
  }

  return (
    <>
      <PromotionalTooltip
        content={<Localized id="refer-a-friend-venue-tooltip-hint" params={{ venueName }} />}
        placement="bottom"
        open={isTooltipOpen}
        onClose={closeTooltip}
      >
        <FavoriteAndShareButtonsBox onClick={() => void handleClick()}>
          <Stack direction="row" alignItems="center" gap={3}>
            <Text variant="xsmall" sx={{ color: 'black', fontWeight: 'bold', display: { xs: 'none', sm: 'initial' } }}>
              <Localized id="refer-a-friend-venue-profile" />
            </Text>
            <Icon src={ArrowsDownToPeopleIcon} size="small" color="midnight" />
          </Stack>
        </FavoriteAndShareButtonsBox>
      </PromotionalTooltip>
      {code && rewardDescription && (
        <VenueReferAFriendOverlay
          isOpen={isDialogOpen}
          toggleDialog={toggleDialog}
          venueName={venueName}
          referralCode={code}
          rewardDescription={rewardDescription}
        />
      )}
      <NoSsr>
        <ErrorSnackbarPortal
          isOpen={hasError}
          errorMessage="Something went wrong. Please try again."
          onClose={onErrorClose}
        />
      </NoSsr>
    </>
  )
}
